/**
 * Servicio API para el sistema de Roles y Permisos de Nemesis Identity.
 *
 * Autenticación: Bearer token desde sessionStorage (react-oidc-context)
 * Tokens obtenidos via getAuthToken() de @/lib/api-client (patrón estándar del proyecto)
 */
import { appConfig } from "@/config/app-config";
import { getAuthToken } from "@/lib/api-client";
import type {
  ApiResponse,
  AuthPermissionDto,
  UserPermissionDto,
  RoleTemplateEntryDto,
  RoleTemplateGroupedDto,
  PaginatedList,
  BulkCreateResult,
  UserRole,
  SetUserRolePayload,
  AddUserPermissionPayload,
  RemoveUserPermissionPayload,
  PutRoleTemplatePayload,
  CreatePermissionPayload,
  UpdatePermissionPayload,
} from "@/types/permissions";

// ─── Helpers ────────────────────────────────────────────────────────────────

function authHeaders(): HeadersInit {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): HeadersInit {
  return { ...authHeaders(), "Content-Type": "application/json" };
}

function identityUrl(path: string): string {
  return appConfig.getIdentityUrl(path);
}

function coreApiUrl(path: string): string {
  return appConfig.getApiUrl(path);
}

/**
 * Wrapper de fetch que normaliza la respuesta a ApiResponse<T>.
 * Para GET no envía Content-Type (como hace la vista de usuarios que funciona).
 * Para POST/PUT/DELETE envía Content-Type: application/json.
 */
async function request<T>(
  url: string,
  opts: RequestInit = {},
): Promise<ApiResponse<T>> {
  try {
    const method = (opts.method ?? "GET").toUpperCase();
    const isGet = method === "GET";
    const h = isGet ? authHeaders() : jsonHeaders();


    const res = await fetch(url, {
      ...opts,
      headers: { ...h, ...(opts.headers as Record<string, string> ?? {}) },
    });

    const text = await res.text();
    let body: any;
    try { body = JSON.parse(text); } catch { body = null; }


    if (!res.ok) {
      return {
        data: null,
        success: false,
        message: body?.message ?? body?.detail ?? body?.title ?? `HTTP ${res.status}`,
        messageType: null,
        statusCode: res.status,
      };
    }

    if (body && typeof body === "object") {
      // Formato con success explícito: { data, success, message, messageType, statusCode }
      if ("success" in body) {
        return body as ApiResponse<T>;
      }
      // Formato ApiResponseDataDto: { data, statusCode, message }
      if ("data" in body && "statusCode" in body) {
        return {
          data: body.data as T,
          success: true,
          message: body.message ?? "Ok",
          messageType: null,
          statusCode: body.statusCode,
        };
      }
      // Formato directo: { lastRow, rows } sin wrapper
      if ("rows" in body) {
        return {
          data: body as T,
          success: true,
          message: "Ok",
          messageType: null,
          statusCode: res.status,
        };
      }
    }

    return {
      data: body as T,
      success: true,
      message: "Ok",
      messageType: null,
      statusCode: res.status,
    };
  } catch (err: any) {
    console.error(`[API] Error:`, err);
    return {
      data: null,
      success: false,
      message: err?.message ?? "Error de red",
      messageType: null,
      statusCode: 0,
    };
  }
}

/**
 * Extrae un array de filas de cualquier formato de respuesta posible:
 * - array directo
 * - { rows: [...] }          (PaginatedList)
 * - { data: { rows: [...] }} (double-wrapped)
 * - { data: [...] }          (wrapped array)
 */
export function extractRows<T>(data: unknown): T[] | null {
  if (!data) return null;
  if (Array.isArray(data)) return data;
  const d = data as Record<string, unknown>;
  if (Array.isArray(d.rows)) return d.rows as T[];
  if (d.data && typeof d.data === "object") {
    if (Array.isArray(d.data)) return d.data as T[];
    const dd = d.data as Record<string, unknown>;
    if (Array.isArray(dd.rows)) return dd.rows as T[];
  }
  return null;
}

// ═════════════════════════════════════════════════════════════════════════════
// ROL DE USUARIO
// ═════════════════════════════════════════════════════════════════════════════

export async function getUserRole(userId: number): Promise<ApiResponse<string>> {
  return request<string>(identityUrl(`users/${userId}/role`));
}

export async function setUserRole(
  userId: number,
  payload: SetUserRolePayload,
): Promise<ApiResponse<string>> {
  return request<string>(identityUrl(`users/${userId}/role`), {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// ═════════════════════════════════════════════════════════════════════════════
// PERMISOS DE USUARIO
// ═════════════════════════════════════════════════════════════════════════════

export async function getUserPermissions(
  userId: number,
): Promise<ApiResponse<PaginatedList<UserPermissionDto>>> {
  const params = new URLSearchParams({
    startRow: "0",
    endRow: "10000",
    sortProperty: "id",
    sortDirection: "asc",
  });
  return request<PaginatedList<UserPermissionDto>>(
    identityUrl(`users/${userId}/permissions`) + "?" + params.toString(),
  );
}

export async function addUserPermission(
  userId: number,
  payload: AddUserPermissionPayload,
): Promise<ApiResponse<UserPermissionDto>> {
  return request<UserPermissionDto>(identityUrl(`users/${userId}/permissions`), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function removeUserPermission(
  userId: number,
  payload: RemoveUserPermissionPayload,
): Promise<ApiResponse<null>> {
  return request<null>(identityUrl(`users/${userId}/permissions`), {
    method: "DELETE",
    body: JSON.stringify(payload),
  });
}

export async function resetUserPermissions(
  userId: number,
): Promise<ApiResponse<PaginatedList<UserPermissionDto>>> {
  return request<PaginatedList<UserPermissionDto>>(
    identityUrl(`users/${userId}/permissions/reset`),
    { method: "POST" },
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PLANTILLAS DE ROL
// ═════════════════════════════════════════════════════════════════════════════

export async function getAllRoleTemplates(): Promise<
  ApiResponse<PaginatedList<RoleTemplateGroupedDto>>
> {
  const params = new URLSearchParams({
    startRow: "0",
    endRow: "10000",
    sortProperty: "role",
    sortDirection: "asc",
  });
  return request<PaginatedList<RoleTemplateGroupedDto>>(
    identityUrl("permissions/role-templates") + "?" + params.toString(),
  );
}

export async function getRoleTemplate(
  role: UserRole,
): Promise<ApiResponse<RoleTemplateGroupedDto>> {
  return request<RoleTemplateGroupedDto>(
    identityUrl(`permissions/role-templates/${role}`),
  );
}

export async function updateRoleTemplate(
  role: UserRole,
  payload: PutRoleTemplatePayload,
): Promise<ApiResponse<RoleTemplateGroupedDto>> {
  return request<RoleTemplateGroupedDto>(
    identityUrl(`permissions/role-templates/${role}`),
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// CATÁLOGO DE PERMISOS
// ═════════════════════════════════════════════════════════════════════════════

export async function getPermissionsCatalog(): Promise<
  ApiResponse<PaginatedList<AuthPermissionDto>>
> {
  const params = new URLSearchParams({
    startRow: "0",
    endRow: "500",
    sortProperty: "entity",
    sortDirection: "asc",
  });
  return request<PaginatedList<AuthPermissionDto>>(
    identityUrl("permissions") + "?" + params.toString(),
  );
}

export async function getPermissionById(
  id: number,
): Promise<ApiResponse<AuthPermissionDto>> {
  return request<AuthPermissionDto>(identityUrl(`permissions/${id}`));
}

export async function createPermission(
  payload: CreatePermissionPayload,
): Promise<ApiResponse<AuthPermissionDto>> {
  return request<AuthPermissionDto>(identityUrl("permissions"), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function bulkCreatePermissions(
  permissions: CreatePermissionPayload[],
): Promise<ApiResponse<BulkCreateResult>> {
  return request<BulkCreateResult>(identityUrl("permissions/bulk"), {
    method: "POST",
    body: JSON.stringify({ permissions }),
  });
}

export async function updatePermission(
  id: number,
  payload: UpdatePermissionPayload,
): Promise<ApiResponse<AuthPermissionDto>> {
  return request<AuthPermissionDto>(identityUrl(`permissions/${id}`), {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/**
 * Sincroniza descripciones y feComponentKeys del catálogo.
 * Recorre todos los permisos y actualiza los que tengan campos vacíos o desactualizados.
 * Retorna cuántos se actualizaron.
 */
export async function syncCatalogMetadata(
  catalog: AuthPermissionDto[],
  generateDesc: (entity: string, action: string) => string,
  generateKey: (entity: string, action: string) => string,
): Promise<{ updated: number; errors: number }> {
  let updated = 0;
  let errors = 0;

  for (const perm of catalog) {
    const expectedDesc = generateDesc(perm.entity, perm.action);
    const expectedKey = generateKey(perm.entity, perm.action);
    const needsUpdate =
      !perm.description || !perm.feComponentKey ||
      perm.description !== expectedDesc || perm.feComponentKey !== expectedKey;

    if (needsUpdate) {
      const res = await updatePermission(perm.id, {
        description: expectedDesc,
        feComponentKey: expectedKey,
      });
      if (res.success) updated++;
      else {
        errors++;
        console.error(`[Permissions] Failed to sync permission ${perm.id} (${perm.entity}:${perm.action}):`, res.message);
      }
    }
  }
  return { updated, errors };
}

export async function deletePermission(
  id: number,
): Promise<ApiResponse<null>> {
  return request<null>(identityUrl(`permissions/${id}`), { method: "DELETE" });
}

// ═════════════════════════════════════════════════════════════════════════════
// USUARIOS
// ═════════════════════════════════════════════════════════════════════════════

export interface UserListItem {
  id: number;
  entityId: number | null;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  role: string;
  isActive: boolean;
  fullName?: string;
}

/**
 * Obtiene todos los usuarios via Identity API (GET con paginación).
 * IMPORTANTE: includeArchived=false e includeRelated=false evitan errores 500 del backend.
 */
export async function getUsers(): Promise<ApiResponse<PaginatedList<UserListItem>>> {
  const params = new URLSearchParams({
    startRow: "0",
    endRow: "2000",
    sortProperty: "username",
    sortDirection: "asc",
    includeArchived: "false",
    includeRelated: "false",
  });
  return request<PaginatedList<UserListItem>>(
    identityUrl("users") + "?" + params.toString(),
  );
}

/**
 * Busca un usuario por email. Carga todos los usuarios y filtra localmente
 * porque el filtro server-side por email no funciona correctamente.
 */
export async function getUserByEmail(email: string): Promise<UserListItem | null> {
  const res = await getUsers();
  if (!res.success || !res.data?.rows) {
    console.warn("[Permissions] getUserByEmail: failed to load users:", res.message);
    return null;
  }
  const lower = email.toLowerCase();
  return res.data.rows.find((u) => u.email?.toLowerCase() === lower) ?? null;
}
